import { State, Action, StateContext, Selector } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
import { LicenseStudyAreaServiceService } from 'src/app/predictor/services/license-study-area-service.service';
import { LicenseStudyAreas } from 'src/app/predictor-enterprise-license/shared/interface/License-Study-Areas';
import { LicenseStudyAreaActions } from './license-study-area.actions';
import { toasty } from 'src/app/global/operators/ngxs-rxjs-toast';

export interface LicenseStudyAreaStateModel {
  studyAreas: LicenseStudyAreas[];
  selectedStudyArea: LicenseStudyAreas | null;
  currentStep: number;
  loading: boolean;
  isStudyAreaNameUnique: boolean | undefined;
}

@State<LicenseStudyAreaStateModel>({
  name: 'licenseStudyArea',
  defaults: {
    studyAreas: [],
    selectedStudyArea: null,
    currentStep: 0,
    loading: false,
    isStudyAreaNameUnique: undefined
  }
})
@Injectable()
export class LicenseStudyAreaState {
  constructor(private readonly service: LicenseStudyAreaServiceService) {}

  @Selector()
  static studyAreas(state: LicenseStudyAreaStateModel) {
    return state.studyAreas;
  }

  @Selector()
  static selectedStudyArea(state: LicenseStudyAreaStateModel) {
    return state.selectedStudyArea;
  }

  @Selector()
  static currentStep(state: LicenseStudyAreaStateModel) {
    return state.currentStep;
  }

  @Selector()
  static isStudyAreaNameUnique(state: LicenseStudyAreaStateModel) {
    return state.isStudyAreaNameUnique;
  }

  @Action(LicenseStudyAreaActions.GetAll)
  getAll(ctx: StateContext<LicenseStudyAreaStateModel>) {
    ctx.patchState({ loading: true });

    return this.service.getAll().pipe(
      tap((studyAreas) => {
        ctx.patchState({ studyAreas, loading: false });
      })
    );
  }

  @Action(LicenseStudyAreaActions.GetById)
  getById(ctx: StateContext<LicenseStudyAreaStateModel>, action: LicenseStudyAreaActions.GetById) {
    return this.service.getById(action.id).pipe(
      tap((studyArea) => {
        ctx.patchState({ studyAreas: studyArea });
      })
    );
  }

@Action(LicenseStudyAreaActions.Create)
create(ctx: StateContext<LicenseStudyAreaStateModel>, action: LicenseStudyAreaActions.Create) {
  return this.service.create(action.studyArea).pipe(
    toasty(
      { success: 'Study Area successfully created for Enterprise License.', error: 'Failed to create Study Area' },
      ctx.dispatch
    ),    
  );
}

  @Action(LicenseStudyAreaActions.Update)
  update(ctx: StateContext<LicenseStudyAreaStateModel>, action: LicenseStudyAreaActions.Update) {
    return this.service.update(action.id, action.studyArea).pipe(
      tap((updated) => {
        const updatedList = ctx.getState().studyAreas.map(sa =>
          sa.id === updated.id ? updated : sa
        );
        ctx.patchState({ studyAreas: updatedList });
      })
    );
  }

  @Action(LicenseStudyAreaActions.Delete)
  delete(ctx: StateContext<LicenseStudyAreaStateModel>, action: LicenseStudyAreaActions.Delete) {
    return this.service.delete(action.id).pipe(
      tap(() => {
        const filtered = ctx.getState().studyAreas.filter(sa => sa.id !== action.id);
        ctx.patchState({ studyAreas: filtered });
      })
    );
  }

  @Action(LicenseStudyAreaActions.SetStep)
  setStep(ctx: StateContext<LicenseStudyAreaStateModel>, action: LicenseStudyAreaActions.SetStep) {
    ctx.patchState({ currentStep: action.step });
  }

  @Action(LicenseStudyAreaActions.SetSelected)
  setSelected(ctx: StateContext<LicenseStudyAreaStateModel>, action: LicenseStudyAreaActions.SetSelected) {
    ctx.patchState({ selectedStudyArea: action.studyArea });
  }

 @Action(LicenseStudyAreaActions.CheckUniqueStudyAreaName)
checkUniqueStudyAreaName(
  ctx: StateContext<LicenseStudyAreaStateModel>,
  action: LicenseStudyAreaActions.CheckUniqueStudyAreaName
) {
    ctx.patchState({ isStudyAreaNameUnique: undefined });
  return this.service.isUniqueStudyAreaNameForGivenLicense(action.studyAreaName, action.licenseNumber).pipe(
    tap((isUnique) => {
      console.log('Unique check result:', isUnique);
      // Save boolean in state
      ctx.patchState({ isStudyAreaNameUnique: isUnique });
    })
  );
}
}
